import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Input } from '@progress/kendo-react-inputs';
import Uploader, { uploaderTypes } from '../../Uploader';
import ListEditor from '../../ListEditor';
import { LocalizationContext } from '../../../contexts';
import './index.scss';
import '../forms.scss';
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { useBanners } from '../../../hooks';
import PublishSwitch from '../publishSwitch';


export default function LinksList({ data, settingsData = {}, onUpdate, validator, ...rest }) {
  const emptyItem = useRef({
    name: "",
    disciplines: [],
    hero: { image: "", link: "", link_target: "_blank" },
  });
  const generalEmptyItem = useRef(JSON.parse(JSON.stringify(emptyItem.current)));
  const personalEmptyItem = useRef({ ...JSON.parse(JSON.stringify(emptyItem.current)) });

  const [compData, setCompData] = useState({});
  const [localization] = useContext(LocalizationContext);
  const { labels } = localization;

  const { disciplines } = useBanners();

  validator.current = () => {
    return true;
  };

  useEffect(() => {
    try {
      let defaultData = { items: [settingsData.type === "general" ? JSON.parse(JSON.stringify(generalEmptyItem.current)) : JSON.parse(JSON.stringify(personalEmptyItem.current))] };
      const startCompData = { ...defaultData, ...JSON.parse(JSON.stringify(data ? data : {})) };
      if (startCompData.items.length === 0) {
        startCompData.items = [settingsData.type === "general" ? JSON.parse(JSON.stringify(generalEmptyItem.current)) : JSON.parse(JSON.stringify(personalEmptyItem.current))];
      }
      setCompData(startCompData);
    }
    catch (e) {

    }
  }, [data]);

  const canPublish = (item) => {
    if (!item.name || !item.hero.image || !item.hero.link) {
      return false;
    }
    return true;
  };

  const isEmptyItem = () => {
    return JSON.stringify(compData) === JSON.stringify(emptyItem.current)
  }

  const showRequired = (requiredField) => {
    return (!isEmptyItem() && !requiredField)
  }

  const updateItem = useCallback((i, newData, key) => {
    let newCompData;
    if (i !== null) {
      newCompData = { ...compData };
      if (key) {
        newCompData.items[i][key] = newData;
      }
      else {
        newCompData.items[i] = { ...newCompData.items[i], ...newData };
      }
      newCompData.items[i].isPublish = newCompData.items[i].isPublish && canPublish(newCompData.items[i]);
    } else {
      newCompData = { ...compData, ...newData };
    }
    setCompData(newCompData);
    onUpdate(newCompData);
  }, [compData]);

  const updateItemHeroBanner = useCallback((i, newData, key) => {
    if (i !== null) {
      let newCompData;
      newCompData = { ...compData };
      if (key) {
        newCompData.items[i].hero[key] = newData;
      }
      else {
        newCompData.items[i].hero = { ...newCompData.items[i].hero, ...newData };
      }
      newCompData.items[i].isPublish = newCompData.items[i].isPublish && canPublish(newCompData.items[i]);
      setCompData(newCompData);
      onUpdate(newCompData);
    }
  }, [compData]);

  const title = (settings) => {
    return settingsData.title ? eval("`" + settingsData.title + "`") : `[${settings.listLength}/${settings.maxSize}]`;
  };

  const itemTitle = (item, settings) => {
    return `${labels.pagesManagement.linkList.banner} ${settings.index + 1}: ${item.name} (${labels.pagesManagement.linkList["banner_type_" + (item.type || "hero")]}) (${!canPublish(item) ? labels.pagesManagement.forms.publish_not_ready : item.isPublish ? labels.pagesManagement.forms.is_publish : labels.pagesManagement.forms.publish_ready})`;
  };

  const itemContent = (item, settings) => {
    return <>
      <div className="form-field">
        <label className="form-field__label mandatory">{labels.pagesManagement.linkList.add_name}</label>
        <Input name={'name'} value={item.name} onChange={({ value }) => updateItem(settings.index, value, 'name')}></Input>
        {showRequired(item.name) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
      </div>

      {disciplines?.length && <div className="form-field">
        <label className="form-field">{labels.pagesManagement.linkList.add_disciplines}</label>
        <MultiSelect
          name={'disciplines'}
          dir={labels.dir}
          data={disciplines}
          value={item.disciplines?.length > 0 ? item.disciplines.map(i => disciplines.find(d => d.id === i)) : []}
          onChange={({ value }) => {
            if (value?.length > 2) return;  //Can only choose up to 2 disciplines
            updateItem(settings.index, value.map(i => i.id), 'disciplines');
          }}
          textField={labels.dimensions_textField}
          dataItemKey="id"
        />
      </div>}

      <div className="form-field">
        <label className="form-field__label mandatory">{labels.pagesManagement.linkList.add_image}</label>
        <Uploader type={uploaderTypes.image} imageUrl={item.hero.image} prefix={'heroBanner'} onChange={(file) => { updateItemHeroBanner(settings.index, { image: file }) }} />
        {showRequired(item.hero.image) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
      </div>

      <div className="form-field">
        <label className="form-field__label mandatory">{labels.pagesManagement.linkList.add_link}</label>
        <div className="form-field--link">
          <Input name={'link'} value={item.hero.link} onChange={({ value }) => updateItemHeroBanner(settings.index, value, 'link')}></Input>
        </div>
        {showRequired(item.hero.link) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
      </div>

      <PublishSwitch isPublish={item.isPublish} canPublish={canPublish(item)}
        updateItem={(event) => updateItem(settings.index, { isPublish: event.target.value })} />
    </>;
  };

  return <ListEditor
    isSortable={true}
    canRemove={true}
    canAdd={true}
    removeContentOnClose={false}
    maxSize={settingsData.maxSize || 3}
    minSize={settingsData.minSize || 0}
    className="linksList-listEditor"
    addNewItemBtnText={labels.pagesManagement.linkList.addNewItemBtnText}
    title={title}
    emptyItem={settingsData.type === "general" ? generalEmptyItem.current : personalEmptyItem.current}
    itemTitle={itemTitle}
    itemContent={itemContent}
    list={[...(compData.items || [])]}
    onListUpdate={(newList) => { updateItem(null, { items: newList }) }}
  />;
};